import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";
import { useComparison } from "../contexts/ComparisonContext";
import { resolveUrl } from "../lib/api";
import { buildWhatsappUrl, getWhatsappMessage } from "../lib/whatsapp";
import { Scale, Check } from "lucide-react";

export default function CarCard({ car, index = 0 }) {
  const { t, currency, lang } = useI18n();
  const { settings } = useSettings();
  const { has, add, remove, canAddMore } = useComparison();
  const rate = settings?.exchange_rate_eur_to_ron || 4.97;
  const priceEUR = car.price_eur;
  const displayPrice = currency === "EUR" ? `€${priceEUR}` : `${Math.round(priceEUR * rate)} RON`;
  const thumb = car.images?.[0]?.url ? resolveUrl(car.images[0].url) : null;
  const inCompare = has(car.id);
  const waEnabled = settings?.contact?.phone && (settings?.floating_contact?.whatsapp_enabled !== false);
  const num = String(index + 1).padStart(2, "0");
  const staggerClass = `card-stagger card-stagger-${Math.min(index + 1, 6)}`;

  const toggleCompare = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (inCompare) remove(car.id); else if (canAddMore) add(car.id);
  };

  const openWhatsapp = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!waEnabled) return;
    const url = buildWhatsappUrl({
      phone: settings.contact.phone,
      message: getWhatsappMessage({ settings, lang, kind: "short", vars: { car_name: `${car.brand} ${car.model || car.name || ""}`.trim() } }),
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Link
      to={`/cars/${car.id}`}
      className={`group block relative ${staggerClass}`}
      data-testid={`car-card-${car.id}`}
    >
      {/* Image container */}
      <div className="relative overflow-hidden bg-[var(--brand-surface-elevated)]" style={{ aspectRatio: "16/10" }}>
        {thumb ? (
          <img
            src={thumb}
            alt={car.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--brand-text-secondary)" }}>
            Fara imagine
          </div>
        )}

        {/* Specs overlay — slides up on hover */}
        <div className="car-slide-overlay">
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs" style={{ color: "rgba(255,255,255,0.72)" }}>
            <span className="uppercase tracking-widest">{t(`common.${car.transmission}`)}</span>
            <span className="uppercase tracking-widest">{t(`common.${car.fuel}`)}</span>
            <span className="uppercase tracking-widest">{car.seats} {lang === "en" ? "seats" : "locuri"}</span>
            {car.year && <span className="uppercase tracking-widest">{car.year}</span>}
          </div>
        </div>

        {/* Compare */}
        <button
          onClick={toggleCompare}
          disabled={!inCompare && !canAddMore}
          className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest backdrop-blur-sm transition-all disabled:opacity-40"
          style={{
            background: inCompare ? "var(--brand-primary)" : "rgba(0,0,0,0.65)",
            color: inCompare ? "#0A0A0A" : "var(--brand-text)",
            border: "1px solid " + (inCompare ? "var(--brand-primary)" : "rgba(255,255,255,0.12)"),
          }}
          title={!inCompare && !canAddMore ? (lang === "en" ? "Compare list full" : "Lista comparare e plina") : ""}
          data-testid={`compare-toggle-${car.id}`}
        >
          {inCompare ? <Check className="w-3 h-3" /> : <Scale className="w-3 h-3" />}
          {inCompare ? (lang === "en" ? "Added" : "Adaugat") : (lang === "en" ? "Compare" : "Compara")}
        </button>

        {/* WhatsApp */}
        {waEnabled && (
          <button
            onClick={openWhatsapp}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 z-10"
            style={{ background: "#25D366", color: "#fff" }}
            aria-label={lang === "en" ? "Ask on WhatsApp" : "Intreaba pe WhatsApp"}
            data-testid={`card-whatsapp-${car.id}`}
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden>
              <path d="M19.11 17.32c-.27-.13-1.59-.78-1.83-.87-.25-.09-.43-.13-.61.13-.18.27-.7.87-.86 1.05-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.47-.07-.13-.61-1.47-.83-2.01-.22-.53-.45-.45-.61-.46-.16-.01-.34-.01-.52-.01-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.1 2.81.13.18 1.9 2.9 4.61 4.07.64.28 1.15.45 1.54.57.65.21 1.24.18 1.71.11.52-.08 1.59-.65 1.81-1.28.22-.62.22-1.16.16-1.28-.07-.13-.25-.2-.52-.34zM16.02 4C9.39 4 4 9.39 4 16.02c0 2.12.55 4.18 1.6 6L4 28l6.13-1.6a11.95 11.95 0 0 0 5.89 1.5h.01c6.63 0 12.02-5.39 12.02-12.02C28.05 9.39 22.66 4 16.02 4zm0 21.94c-1.86 0-3.69-.5-5.29-1.45l-.38-.22-3.65.96.97-3.55-.25-.39a9.94 9.94 0 0 1-1.55-5.33c0-5.49 4.47-9.96 9.96-9.96s9.96 4.47 9.96 9.96-4.47 9.98-9.97 9.98z" />
            </svg>
          </button>
        )}
      </div>

      {/* Gold line — grows on hover */}
      <div className="car-bottom-line" />

      {/* Card info */}
      <div className="pt-5">
        <div className="label-eyebrow mb-1.5">{car.brand}</div>
        <h3 className="font-serif font-light text-[1.65rem] leading-tight mb-5" style={{ color: "var(--brand-text)" }}>
          {car.name}
        </h3>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="label-eyebrow mb-1">{t("common.from")}</div>
            <div className="font-serif text-[1.55rem]" style={{ color: "var(--brand-primary)" }}>
              {displayPrice}
              <span className="text-xs ml-1.5 font-sans" style={{ color: "var(--brand-text-secondary)", fontFamily: "'Manrope', sans-serif" }}>
                {t("common.perDay")}
              </span>
            </div>
          </div>
          <span
            className="label-eyebrow transition-colors duration-300"
            style={{ color: "var(--brand-text-secondary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--brand-text-secondary)")}
          >
            {t("common.details")} →
          </span>
        </div>
      </div>
    </Link>
  );
}
